import React, { useState } from "react";
import { toast } from "sonner";
import { useJourney } from "@/context/JourneyContext";
import { StageFrame, Eyebrow } from "@/components/StageFrame";
import { ConsultantNav } from "@/components/Navigation";
import { api, money } from "@/lib/api";
import { Home, Wallet, Landmark } from "lucide-react";

const USABLE_LVR = 0.8;   // banks lend up to 80% of home value
const DEPOSIT_PCT = 0.2;  // deposit is 20% of the total price, drawn from equity

const Field = ({ label, value, onChange, testId, placeholder }) => (
  <div>
    <label className="label-eyebrow text-mutedText">{label}</label>
    <div className="relative mt-1.5">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-mutedText">$</span>
      <input data-testid={testId} type="number" min="0" step="1000" value={value}
        onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full rounded-xl border border-line bg-white/5 pl-7 pr-3 py-3 text-forest focus:outline-none focus:ring-2 focus:ring-brand" />
    </div>
  </div>
);

export default function InvestorFunding() {
  const { session, data, next, back, mergeData } = useJourney();
  const loc = data.investor_location || {};
  const fp = data.investor_floorplan || {};
  const f0 = data.investor_funding || {};

  // Total price = selected house price + estimated land price (from Floorplan stage).
  const totalPrice = fp.total_price || ((fp.house_price || fp.base_price || 0) + (fp.land_price || 0)) || loc.median || 700000;

  const [homeValue, setHomeValue] = useState(f0.current_home_value ?? "");
  const [loanBalance, setLoanBalance] = useState(f0.current_loan_balance ?? "");

  // Deposit is a fixed 20% of the total price, drawn from usable home equity.
  const deposit = DEPOSIT_PCT * totalPrice;
  const loan = (1 - DEPOSIT_PCT) * totalPrice;           // 80% interest-only investment loan
  const lvr = 80;
  const usableEquity = Math.max(0, USABLE_LVR * (Number(homeValue) || 0) - (Number(loanBalance) || 0));
  const equityCovers = usableEquity >= deposit;

  const saveNext = () => {
    const investor_funding = {
      current_home_value: Number(homeValue) || 0,
      current_loan_balance: Number(loanBalance) || 0,
      usable_equity: Math.round(usableEquity),
      house_price: Math.round(fp.house_price || fp.base_price || 0),
      land_price: Math.round(fp.land_price || 0),
      property_value: Math.round(totalPrice),
      deposit: Math.round(deposit),
      equity_drawn: Math.round(deposit),
      loan: Math.round(loan),
      loan_amount: Math.round(loan),
      lvr,
      equity_covers: equityCovers,
    };
    mergeData({ investor_funding });
    if (session) api.patchSession(session.id, { data: { investor_funding } }).catch(() => {});
    next();
  };

  return (
    <StageFrame testId="stage-investor-funding" className="px-6 md:px-14 pt-16 pb-28">
      <div className="max-w-5xl mx-auto">
        <Eyebrow>How you'll fund it</Eyebrow>
        <h1 className="font-serif text-3xl md:text-5xl font-light tracking-tight text-forest mt-3">
          Turning equity into your 20% deposit.
        </h1>
        <p className="text-mutedText mt-3 max-w-2xl">
          House &amp; land total: <span className="text-forest font-medium">{money(totalPrice)}</span>. The deposit is a fixed 20% ({money(deposit)}) drawn from your home equity; the remaining 80% is an interest-only investment loan.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8 items-start">
          <div className="space-y-5">
            <div className="rounded-2xl border border-line bg-white/[0.03] p-5">
              <div className="flex items-center gap-2 text-brand"><Home size={18} /><span className="label-eyebrow text-forest/70">Your current home</span></div>
              <p className="text-mutedText text-sm mt-1.5">We use up to 80% of your home value to check you have enough usable equity for the 20% deposit.</p>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Current home value" value={homeValue} onChange={setHomeValue} testId="funding-home-value" placeholder="900,000" />
                <Field label="Current loan balance" value={loanBalance} onChange={setLoanBalance} testId="funding-loan-balance" placeholder="400,000" />
              </div>
            </div>

            <div className="rounded-2xl border border-line bg-white/[0.03] p-5">
              <div className="flex items-center gap-2 text-brand"><Wallet size={18} /><span className="label-eyebrow text-forest/70">House &amp; land breakdown</span></div>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-mutedText">House{fp.name ? ` (${fp.name})` : ""}</span><span className="text-forest font-medium">{money(fp.house_price || fp.base_price || 0)}</span></div>
                <div className="flex justify-between"><span className="text-mutedText">Land (estimated)</span><span className="text-forest font-medium">{money(fp.land_price || 0)}</span></div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-line bg-white/5 p-6" data-testid="funding-summary">
            <div className="flex items-center gap-2 text-brand"><Landmark size={18} /><span className="label-eyebrow text-forest/70">Your funding position</span></div>
            <div className="mt-4 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-mutedText">House &amp; land total</span><span className="text-forest font-medium">{money(totalPrice)}</span></div>
              <div className="flex justify-between"><span className="text-mutedText">Deposit (20%, drawn from equity)</span><span className="text-forest font-medium" data-testid="funding-total-deposit">{money(deposit)}</span></div>
              <div className="flex justify-between"><span className="text-mutedText">Usable equity available (80% LVR − loan)</span><span className="text-forest font-medium" data-testid="funding-usable-equity">{money(usableEquity)}</span></div>
              <div className="flex justify-between border-t border-line pt-3"><span className="text-forest">Interest-only investment loan (80%)</span><span className="text-brand font-serif text-xl" data-testid="funding-loan-amount">{money(loan)}</span></div>
              <div className="flex justify-between"><span className="text-mutedText">LVR</span><span className="font-medium text-emerald-300" data-testid="funding-lvr">{lvr.toFixed(1)}%</span></div>
            </div>
            {!equityCovers && (Number(homeValue) > 0) && (
              <p className="mt-3 text-xs text-red-300/80" data-testid="funding-equity-warning">
                Your usable equity ({money(usableEquity)}) doesn't yet cover the 20% deposit ({money(deposit)}). You may need extra savings or a higher-value security.
              </p>
            )}
          </div>
        </div>
      </div>

      <ConsultantNav testId="stage-investor-funding" onBack={back} onNext={saveNext} nextLabel="See your numbers" />
    </StageFrame>
  );
}
